/**
 * Types pour la gestion des sous-traitants
 * Carnet d'adresses + Contrats ST + Suivi Prévu vs Facturé
 */

// ============================================================
// INTERFACE: SOUS-TRAITANT (Carnet d'adresses)
// ============================================================

export interface SousTraitant {
  id: string;
  entreprise_id: string;

  // Identité
  nom: string;
  siret?: string | null;
  adresse?: string | null;
  telephone?: string | null;
  email?: string | null;
  contact_nom?: string | null;

  // Métier
  specialite?: SpecialiteST | null;

  // Tarification indicative
  taux_journalier?: number | null;

  // Statut
  statut: 'actif' | 'inactif';

  // Notes
  notes?: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
}

// Type pour la création d'un sous-traitant
export interface SousTraitantCreate {
  entreprise_id: string;
  nom: string;
  siret?: string | null;
  adresse?: string | null;
  telephone?: string | null;
  email?: string | null;
  contact_nom?: string | null;
  specialite?: SpecialiteST | null;
  taux_journalier?: number | null;
  notes?: string | null;
}

// Type pour la mise à jour d'un sous-traitant
export type SousTraitantUpdate = Partial<Omit<SousTraitant, 'id' | 'entreprise_id' | 'created_at' | 'updated_at'>>;

// ============================================================
// INTERFACE: CONTRAT SOUS-TRAITANCE
// ============================================================

export interface ContratSousTraitance {
  id: string;
  chantier_id: string;
  sous_traitant_id: string;

  // Périmètre
  description_travaux?: string | null;

  // Montant
  montant_contrat_ht: number;

  // Planning
  date_debut_prevue?: string | null;
  date_fin_prevue?: string | null;

  // Statut
  statut: StatutContratST;

  // Timestamps
  created_at: string;
  updated_at: string;

  // Jointure (optionnelle)
  sous_traitant?: SousTraitant;
}

// Type pour la création d'un contrat
export interface ContratSTCreate {
  chantier_id: string;
  sous_traitant_id: string;
  description_travaux?: string | null;
  montant_contrat_ht: number;
  taux_tva?: number;
  date_debut_prevue?: string | null;
  date_fin_prevue?: string | null;
  statut?: StatutContratST;
}

// Type pour la mise à jour d'un contrat
export type ContratSTUpdate = Partial<Omit<ContratSousTraitance, 'id' | 'chantier_id' | 'sous_traitant_id' | 'created_at' | 'updated_at'>>;

// ============================================================
// INTERFACE: CONTRAT ST AVEC TOTAUX CALCULÉS
// ============================================================
// Retourné par la fonction get_chantier_contrats_st

export interface ContratSTAvecTotaux {
  contrat_id: string;
  sous_traitant_id: string;
  sous_traitant_nom: string;
  sous_traitant_specialite?: string | null;
  description_travaux?: string | null;
  montant_contrat_ht: number;
  montant_facture_ht: number;
  reste_a_facturer: number;
  pourcentage_facture: number;
  statut: StatutContratST;
  date_debut_prevue?: string | null;
  date_fin_prevue?: string | null;
  nb_factures: number;
}

// ============================================================
// TYPES: STATUTS ET SPÉCIALITÉS
// ============================================================

export type StatutContratST = 'a_demarrer' | 'en_cours' | 'termine' | 'annule';

export type SpecialiteST =
  | 'electricite'
  | 'plomberie'
  | 'maconnerie'
  | 'peinture'
  | 'menuiserie'
  | 'carrelage'
  | 'platrerie'
  | 'chauffage'
  | 'climatisation'
  | 'couverture'
  | 'charpente'
  | 'serrurerie'
  | 'vitrerie'
  | 'terrassement'
  | 'demolition'
  | 'gros_oeuvre'
  | 'etancheite'
  | 'isolation'
  | 'autre';

// ============================================================
// CONSTANTES: LISTES POUR SELECTS
// ============================================================

export const SPECIALITES_ST: { value: SpecialiteST; label: string }[] = [
  { value: 'electricite', label: 'Électricité' },
  { value: 'plomberie', label: 'Plomberie' },
  { value: 'maconnerie', label: 'Maçonnerie' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'menuiserie', label: 'Menuiserie' },
  { value: 'carrelage', label: 'Carrelage' },
  { value: 'platrerie', label: 'Plâtrerie' },
  { value: 'chauffage', label: 'Chauffage' },
  { value: 'climatisation', label: 'Climatisation' },
  { value: 'couverture', label: 'Couverture' },
  { value: 'charpente', label: 'Charpente' },
  { value: 'serrurerie', label: 'Serrurerie' },
  { value: 'vitrerie', label: 'Vitrerie' },
  { value: 'terrassement', label: 'Terrassement' },
  { value: 'demolition', label: 'Démolition' },
  { value: 'gros_oeuvre', label: 'Gros œuvre' },
  { value: 'etancheite', label: 'Étanchéité' },
  { value: 'isolation', label: 'Isolation' },
  { value: 'autre', label: 'Autre' },
];

export const STATUTS_CONTRAT_ST: { value: StatutContratST; label: string; color: string }[] = [
  { value: 'a_demarrer', label: 'À démarrer', color: 'bg-blue-500' },
  { value: 'en_cours', label: 'En cours', color: 'bg-yellow-500' },
  { value: 'termine', label: 'Terminé', color: 'bg-green-500' },
  { value: 'annule', label: 'Annulé', color: 'bg-gray-500' },
];

// Type de facture ST (utilisé dans le modal coûts annexes)
export type TypeFactureST = 'acompte' | 'facture' | 'solde';

export const TYPES_FACTURE_ST: { value: TypeFactureST; label: string }[] = [
  { value: 'acompte', label: 'Acompte' },
  { value: 'facture', label: 'Facture intermédiaire' },
  { value: 'solde', label: 'Solde' },
];

// ============================================================
// INTERFACE: FACTURE LIÉE À UN CONTRAT ST
// ============================================================

export interface FactureContratST {
  id: string;
  contrat_st_id: string;
  date_facture: string;
  montant_ht: number;
  description?: string | null;
  type_facture_st?: TypeFactureST | null;
  numero_facture?: string | null;
  created_at: string;
}

// ============================================================
// HELPERS
// ============================================================

/**
 * Retourne le label d'une spécialité ST
 */
export function getSpecialiteLabel(specialite?: SpecialiteST | null): string {
  if (!specialite) return '';
  return SPECIALITES_ST.find(s => s.value === specialite)?.label ?? specialite;
}

/**
 * Retourne le label d'un statut de contrat ST
 */
export function getStatutContratLabel(statut: StatutContratST): string {
  return STATUTS_CONTRAT_ST.find(s => s.value === statut)?.label ?? statut;
}

/**
 * Retourne la couleur d'un statut de contrat ST
 */
export function getStatutContratColor(statut: StatutContratST): string {
  return STATUTS_CONTRAT_ST.find(s => s.value === statut)?.color ?? 'bg-gray-500';
}
